import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const cardVariants = cva(
  "relative overflow-hidden bg-card text-card-foreground transition-all duration-300 w-full"  ,
  {
    variants: {
      variant: {
        default: "border border-border shadow-sm hover:shadow-md hover:border-border/80 rounded-[var(--radius-lg)]",
        elevated: "border border-border/60 shadow-lg hover:shadow-xl rounded-[var(--radius-lg)]",
        glass: "bg-background/80 backdrop-blur-sm border border-border/30 hover:border-border/50 shadow-sm hover:shadow-md rounded-[var(--radius-lg)]",
        "glass-heavy": "bg-background/95 backdrop-blur-md border border-border/20 hover:border-border/40 shadow-md hover:shadow-lg rounded-[var(--radius-lg)]",
        glow: "border border-primary/20 hover:border-primary/40 shadow-lg hover:shadow-xl shadow-primary/10 hover:shadow-primary/20 rounded-[var(--radius-lg)]",
        gradient: "bg-gradient-to-br from-primary/5 to-secondary/5 border border-transparent shadow-md hover:shadow-lg rounded-[var(--radius-lg)]",
        premium: "border border-primary/30 hover:border-primary/50 shadow-xl hover:shadow-2xl bg-gradient-to-br from-card to-card/95 rounded-[var(--radius-lg)]",
        feature: "border border-border/40 shadow-md hover:shadow-lg hover:scale-[1.02] hover:-translate-y-1 rounded-[var(--radius-lg)]",
        metric: "border border-border/50 bg-gradient-to-br from-card to-muted/30 shadow-sm hover:shadow-md rounded-[var(--radius-lg)]",
        testimonial: "border border-border/30 bg-gradient-to-br from-card/95 to-card shadow-md italic rounded-[var(--radius-lg)]",
        pricing: "border-2 border-border/60 hover:border-primary/60 shadow-lg hover:shadow-xl rounded-[var(--radius-lg)]",
        "neo-brutal": "border-[3px] border-foreground shadow-[4px_4px_0px_0px_hsl(var(--foreground))] hover:shadow-[6px_6px_0px_0px_hsl(var(--foreground))] hover:translate-x-[-2px] hover:translate-y-[-2px] rounded-none"
      },
      size: {
        sm: "p-3 sm:p-4 lg:p-5",
        default: "p-4 sm:p-6 lg:p-8",
        lg: "p-6 sm:p-8 lg:p-10",
        xl: "p-8 sm:p-10 lg:p-12",
        responsive: "p-4 xs:p-5 sm:p-6 md:p-8 lg:p-10"
      },
      interactive: {
        true: "cursor-pointer transition-transform",
        false: ""
      },
      shimmer: {
        true: "group relative",
        false: ""
      },
      aspectRatio: {
        none: "",
        square: "aspect-square",
        video: "aspect-video",
        portrait: "aspect-[3/4]",
        landscape: "aspect-[4/3]",
        wide: "aspect-[16/9]",
        ultrawide: "aspect-[21/9]"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      interactive: false,
      shimmer: false,
      aspectRatio: "none"
    },
  }
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  hover?: boolean;
  glow?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, size, interactive, shimmer, aspectRatio, hover = true, glow = false, children, ...props }, ref) => {
    // Apply neo-brutal variant automatically in neo-brutal theme
    const isNeoBrutal = typeof document !== 'undefined' && document.documentElement.classList.contains('neo-brutal');
    const effectiveVariant = isNeoBrutal && variant === 'default' ? 'neo-brutal' : variant;
    
    return (
      <div
        ref={ref}
        className={cn(
          cardVariants({ variant: effectiveVariant, size, interactive, shimmer, aspectRatio }),
          hover === false && "hover:shadow-none hover:scale-100 hover:translate-y-0",
          glow && "shadow-glow",
          className
        )}
        {...props}
      >
        {shimmer && (
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-500">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 animate-shimmer" />
          </div>
        )}
        {glow && (
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent blur-xl" />
          </div>
        )}
        {children}
      </div>
    );
  }
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-[var(--spacing-sm)]", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-lg sm:text-xl lg:text-2xl font-semibold leading-[var(--line-height-tight)] tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm sm:text-base text-muted-foreground leading-[var(--line-height-normal)]", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("pt-[var(--spacing-md)]", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center pt-[var(--spacing-lg)]", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

// Additional card utilities
const CardIcon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: 'default' | 'primary' | 'success' | 'warning' | 'destructive' | 'info';
    size?: 'sm' | 'default' | 'lg';
  }
>(({ className, variant = 'default', size = 'default', children, ...props }, ref) => {
  const iconVariants = {
    default: "bg-muted text-muted-foreground",
    primary: "bg-primary/10 text-primary",
    success: "bg-success/10 text-success",
    warning: "bg-warning/10 text-warning",
    destructive: "bg-destructive/10 text-destructive",
    info: "bg-info/10 text-info"
  };
  
  const sizeVariants = {
    sm: "w-8 h-8 sm:w-10 sm:h-10",
    default: "w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14",
    lg: "w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16"
  };
  
  return (
    <div
      ref={ref}
      className={cn(
        "rounded-[var(--radius-base)] flex items-center justify-center",
        iconVariants[variant],
        sizeVariants[size],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
CardIcon.displayName = "CardIcon";

const CardBadge = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  }
>(({ className, position = 'top-right', children, ...props }, ref) => {
  const positionClasses = {
    'top-left': 'top-2 left-2 sm:top-3 sm:left-3',
    'top-right': 'top-2 right-2 sm:top-3 sm:right-3',
    'bottom-left': 'bottom-2 left-2 sm:bottom-3 sm:left-3',
    'bottom-right': 'bottom-2 right-2 sm:bottom-3 sm:right-3'
  };
  
  return (
    <div
      ref={ref}
      className={cn(
        "absolute px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold z-10 shadow-sm",
        positionClasses[position],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
CardBadge.displayName = "CardBadge";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, CardIcon, CardBadge }
